import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
  YoutubeIcon,
} from '@/components/ui/icons/icons-new'
import { AI_LABEL_TEXT, aiLabelGeometry, aiLabelOffset } from '@backend/lib/util/aiLabelSpec'
import { buildAiLabelSvg } from '@backend/lib/util/aiLabelSvg'
import {
  AI_DISCLOSURE_CAPABILITY,
  AI_LABEL_POSITIONS,
  DEFAULT_AI_LABEL_STYLE,
  resolveAiDisclosurePlan,
  type AiDisclosureChoice,
  type AiDisclosurePlatform,
  type AiLabelPosition,
  type AiLabelStyle,
  type AiProvenanceDetection,
} from '@backend/lib/platforms/aiDisclosure'

/**
 * The one place a user decides how a post discloses AI involvement.
 *
 * Disclosure used to live in each platform's own "Advanced" section, so the same
 * decision was made up to five times with different wording, and the platforms
 * that need something other than a flag said nothing at all.
 *
 * Two independent switches rather than a ladder. Live testing showed the two
 * mechanisms are not weak and strong versions of one thing: Instagram's and
 * Facebook's own labels appear on the post surface, while a baked pill is the
 * only mark that survives a reshare or a download. Ordering them forced a choice
 * that isn't one.
 *
 * The per-platform list is generated from the same resolver the publish path
 * calls, so the explanation cannot drift from the behaviour — and it is the only
 * thing that tells a user when a switch will do nothing on a given platform.
 */

type Props = {
  choice: AiDisclosureChoice
  onChoiceChange: (choice: AiDisclosureChoice) => void
  /** Only platforms selected for this post are listed. */
  activePlatforms: ReadonlyArray<AiDisclosurePlatform>
  /** Whether the post has at least one image. Video can't carry a baked label,
   *  so the per-platform lines have to say so rather than promise one. */
  hasImageMedia: boolean
  /**
   * Whether media provenance set the default. Drives the "we turned this on for
   * you" note so an automatic default doesn't look like a setting the user
   * forgot they changed.
   */
  autoDetected: boolean
  /** What we found in the media, so the app's assumption is visible. */
  detected: AiProvenanceDetection
  /** The brand's saved label style, if any. Drives whether "Save as my default"
   *  is offered and whether it has anything to clear. */
  savedStyle?: AiLabelStyle | null
  onSaveStyleAsDefault?: (style: AiLabelStyle) => void
  onClearSavedStyle?: () => void
}

/** The platform icons take an explicit `color` (defaulting to white) rather
 *  than a className, so we pass `currentColor` and let the wrapper's Tailwind
 *  text colour drive it. That keeps them correct in both themes. */
type PlatformIcon = (props: {
  color?: string
  width?: number
  height?: number
}) => React.JSX.Element

const PLATFORM_META: Record<AiDisclosurePlatform, { label: string; Icon: PlatformIcon }> = {
  instagram: { label: 'Instagram', Icon: InstagramIcon },
  facebook: { label: 'Facebook', Icon: FacebookIcon },
  linkedin: { label: 'LinkedIn', Icon: LinkedinIcon },
  tiktok: { label: 'TikTok', Icon: TikTokIcon },
  youtube: { label: 'YouTube', Icon: YoutubeIcon },
}

/** Display order is fixed rather than following selection order, so the list
 *  doesn't reshuffle as the user toggles platforms. */
const DISPLAY_ORDER: ReadonlyArray<AiDisclosurePlatform> = [
  'instagram',
  'facebook',
  'linkedin',
  'tiktok',
  'youtube',
]

/** What the app assumes, stated plainly. Deliberately describes only what we
 *  found — it never tells the user what they ought to do, because whether an
 *  image would pass as authentic is a judgement about content we can't make. */
const DETECTION_COPY: Record<AiProvenanceDetection, { title: string; detail: string }> = {
  generated: {
    title: 'This media was generated with AI',
    detail: 'Cassi created it, so we assume disclosure is needed and switch the platform label on.',
  },
  edited: {
    title: 'This media was edited with AI',
    detail:
      'The original is yours; AI changed part of it. We assume disclosure is needed and describe it as edited rather than generated.',
  },
  none: {
    title: 'No AI detected in this media',
    detail:
      'You uploaded it and Cassi has not altered it, so nothing is disclosed unless you switch it on. You can still label it if you know AI was involved elsewhere.',
  },
}

const POSITION_LABELS: Record<AiLabelPosition, string> = {
  bottomLeft: 'Bottom left',
  bottomRight: 'Bottom right',
  topLeft: 'Top left',
  topRight: 'Top right',
}

/**
 * Live preview of the label, drawn from the same geometry and SVG builder the
 * publish-time baker uses.
 *
 * Geometry is resolved against a nominal 1080² image rather than the swatch's
 * own pixel size, because `aiLabelGeometry` scales with the image's short side
 * and clamps in absolute pixels. Sizing it off a 200px-wide swatch would hit the
 * floor immediately and show a label far larger, relative to the frame, than the
 * real one. Every offset is then expressed as a percentage of that nominal
 * image, so the swatch is a faithful scale model.
 */
const NOMINAL_IMAGE = 1080

const LabelSwatch = ({ style }: { style: AiLabelStyle }) => {
  const text = style.text.trim() || AI_LABEL_TEXT
  const geometry = aiLabelGeometry(NOMINAL_IMAGE, NOMINAL_IMAGE, text, style.scale)
  const svg = buildAiLabelSvg(text, geometry, {
    backgroundColor: style.backgroundColor,
    textColor: style.textColor,
    backgroundAlpha: style.backgroundAlpha,
  })
  const { x, y } = aiLabelOffset(NOMINAL_IMAGE, NOMINAL_IMAGE, geometry, style.position)

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium">Preview</p>
      {/* A neutral checkerboard, not a flat fill: the pill is translucent, so a
          plain background would hide exactly the legibility problem a user is
          adjusting opacity to solve. */}
      <div
        className="border-border relative aspect-square w-full overflow-hidden rounded-md border"
        style={{
          backgroundImage:
            'linear-gradient(45deg, rgba(127,127,127,0.25) 25%, transparent 25%, transparent 75%, rgba(127,127,127,0.25) 75%), linear-gradient(45deg, rgba(127,127,127,0.25) 25%, transparent 25%, transparent 75%, rgba(127,127,127,0.25) 75%)',
          backgroundSize: '16px 16px',
          backgroundPosition: '0 0, 8px 8px',
        }}>
        <img
          src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
          alt=""
          aria-hidden
          className="absolute select-none"
          style={{
            left: `${(x / NOMINAL_IMAGE) * 100}%`,
            top: `${(y / NOMINAL_IMAGE) * 100}%`,
            width: `${(geometry.width / NOMINAL_IMAGE) * 100}%`,
          }}
        />
      </div>
      <p className="text-foreground text-xs opacity-80">
        Shown on a 1080 × 1080 post. Scales with the image, so a taller or wider post gets a
        proportionally similar label.
      </p>
    </div>
  )
}

/** Turns a resolved plan into the sentence shown next to a platform. Built from
 *  the same resolver the publish path calls, so it describes what will actually
 *  happen rather than what we intended. */
const describePlan = (
  platform: AiDisclosurePlatform,
  choice: AiDisclosureChoice,
  hasImageMedia: boolean,
): string => {
  const { nativeFlag, bakedLabel, cannotDisclose } = resolveAiDisclosurePlan(
    choice,
    platform,
    hasImageMedia,
  )
  const { summary, publishes } = AI_DISCLOSURE_CAPABILITY[platform]

  const parts: string[] = []
  if (nativeFlag) parts.push(summary)
  if (bakedLabel) parts.push('Visible label added to the image.')

  // Say so outright when this platform will not disclose. We used to quietly
  // append a line to the caption instead, which edited the user's own copy. The
  // honest version tells them and lets them turn the other switch on.
  if (cannotDisclose) {
    if (!publishes.includes('image')) {
      parts.push('Video only, so no visible label is possible. Nothing will disclose here.')
    } else if (choice.visibleLabel && !hasImageMedia) {
      parts.push('No image in this post to label, so nothing will disclose here.')
    } else if (!choice.visibleLabel) {
      parts.push('No AI label available here. Turn on “Add a visible label” to disclose.')
    } else {
      parts.push('Nothing can disclose on this platform for this post.')
    }
  }

  // The visible label was asked for and cannot be delivered, but the platform's
  // own flag still covers the post, so this is a downgrade rather than silence.
  if (choice.visibleLabel && !bakedLabel && nativeFlag) {
    parts.push(
      publishes.includes('image')
        ? 'No image in this post to label.'
        : 'Video only, so no image can be labelled.',
    )
  }

  return parts.join(' ')
}

export const AiContentLabelSection = ({
  choice,
  onChoiceChange,
  activePlatforms,
  hasImageMedia,
  autoDetected,
  detected,
  savedStyle,
  onSaveStyleAsDefault,
  onClearSavedStyle,
}: Props) => {
  const listed = DISPLAY_ORDER.filter((p) => activePlatforms.includes(p))
  const style = choice.labelStyle ?? DEFAULT_AI_LABEL_STYLE

  const setStyle = (patch: Partial<AiLabelStyle>) =>
    onChoiceChange({ ...choice, labelStyle: { ...style, ...patch } })

  const savedMatchesCurrent = Boolean(
    savedStyle && JSON.stringify(savedStyle) === JSON.stringify(style),
  )

  return (
    <div className="space-y-4">
      {/* States the app's assumption before the switches, so an automatic
          default is legible and a manual override is an informed one. */}
      <div className="bg-muted rounded-lg p-3">
        <p className="text-foreground text-xs font-medium">{DETECTION_COPY[detected].title}</p>
        <p className="text-foreground mt-0.5 text-xs opacity-80">
          {DETECTION_COPY[detected].detail}
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-start gap-3">
          <Checkbox
            id="ai-native-label"
            className="mt-0.5"
            checked={choice.nativeLabel}
            onCheckedChange={(checked: boolean) =>
              onChoiceChange({ ...choice, nativeLabel: checked })
            }
          />
          <div className="space-y-1">
            <Label htmlFor="ai-native-label" className="cursor-pointer text-sm font-medium">
              Label as AI content where possible
            </Label>
            <p className="text-muted-foreground text-xs">
              {autoDetected
                ? 'Your media was generated or edited with AI, so we turned this on. Uncheck it if you consider the changes non-substantive.'
                : 'Asks each platform to apply its own AI label. Your artwork is untouched. Not every platform can do this for every post type — the list below says which.'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            id="ai-visible-label"
            className="mt-0.5"
            checked={choice.visibleLabel}
            onCheckedChange={(checked: boolean) =>
              onChoiceChange({ ...choice, visibleLabel: checked })
            }
          />
          <div className="space-y-1">
            <Label htmlFor="ai-visible-label" className="cursor-pointer text-sm font-medium">
              Add a visible label to the image
            </Label>
            <p className="text-muted-foreground text-xs">
              Renders a label into the image itself, so it survives resharing and downloads. Images
              only. The stored file is never changed — this is applied per post.
            </p>
          </div>
        </div>

        {choice.visibleLabel && (
          <div className="ml-7 space-y-2 border-l pl-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="ai-label-in-preview"
                className="mt-0.5"
                checked={choice.displayInPreview}
                onCheckedChange={(checked: boolean) =>
                  onChoiceChange({ ...choice, displayInPreview: checked })
                }
              />
              <div className="space-y-1">
                <Label htmlFor="ai-label-in-preview" className="cursor-pointer text-sm">
                  Display in preview
                </Label>
                <p className="text-muted-foreground text-xs">
                  {choice.displayInPreview
                    ? 'The post preview shows the label exactly where it will be baked in.'
                    : 'The preview will show a clean image even though the published post carries a label.'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="ai-label-in-workspace"
                className="mt-0.5"
                checked={choice.displayInWorkspace}
                onCheckedChange={(checked: boolean) =>
                  onChoiceChange({ ...choice, displayInWorkspace: checked })
                }
              />
              <div className="space-y-1">
                <Label htmlFor="ai-label-in-workspace" className="cursor-pointer text-sm">
                  Display in workspace
                </Label>
                <p className="text-muted-foreground text-xs">
                  Also draws it over the media in the editor. Off by default so it does not sit on
                  top of the artwork while you work.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {choice.visibleLabel && (
        <div className="bg-muted space-y-3 rounded-lg p-3">
          <p className="text-foreground text-xs font-medium">Label appearance</p>

          <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ai-label-text" className="text-xs font-medium">
                    Text
                  </Label>
                  <Input
                    id="ai-label-text"
                    value={style.text}
                    maxLength={60}
                    onChange={(e) => setStyle({ text: e.target.value })}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="ai-label-position" className="text-xs font-medium">
                    Position
                  </Label>
                  <Select
                    value={style.position}
                    onValueChange={(value) => setStyle({ position: value as AiLabelPosition })}>
                    <SelectTrigger id="ai-label-position">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AI_LABEL_POSITIONS.map((position) => (
                        <SelectItem key={position} value={position}>
                          {POSITION_LABELS[position]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ai-label-scale" className="text-xs font-medium">
                    Size — {Math.round(style.scale * 100)}%
                  </Label>
                  <input
                    id="ai-label-scale"
                    type="range"
                    min={0.25}
                    max={4}
                    step={0.05}
                    value={style.scale}
                    onChange={(e) => setStyle({ scale: Number(e.target.value) })}
                    className="accent-primary w-full"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="ai-label-alpha" className="text-xs font-medium">
                    Background opacity — {Math.round(style.backgroundAlpha * 100)}%
                  </Label>
                  <input
                    id="ai-label-alpha"
                    type="range"
                    min={0}
                    max={1}
                    step={0.02}
                    value={style.backgroundAlpha}
                    onChange={(e) => setStyle({ backgroundAlpha: Number(e.target.value) })}
                    className="accent-primary w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ai-label-bg" className="text-xs font-medium">
                    Background
                  </Label>
                  <input
                    id="ai-label-bg"
                    type="color"
                    value={style.backgroundColor}
                    onChange={(e) => setStyle({ backgroundColor: e.target.value })}
                    className="border-border h-9 w-full cursor-pointer rounded-md border bg-transparent"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="ai-label-fg" className="text-xs font-medium">
                    Text colour
                  </Label>
                  <input
                    id="ai-label-fg"
                    type="color"
                    value={style.textColor}
                    onChange={(e) => setStyle({ textColor: e.target.value })}
                    className="border-border h-9 w-full cursor-pointer rounded-md border bg-transparent"
                  />
                </div>
              </div>
            </div>

            <LabelSwatch style={style} />
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {onSaveStyleAsDefault && (
              <button
                type="button"
                onClick={() => onSaveStyleAsDefault(style)}
                disabled={savedMatchesCurrent}
                className="text-foreground text-xs underline opacity-80 hover:opacity-100 disabled:cursor-default disabled:no-underline disabled:opacity-50">
                {savedMatchesCurrent ? 'Saved as your default' : 'Save as my default'}
              </button>
            )}
            <button
              type="button"
              onClick={() => onChoiceChange({ ...choice, labelStyle: DEFAULT_AI_LABEL_STYLE })}
              className="text-foreground text-xs underline opacity-80 hover:opacity-100">
              Reset to CASSI defaults
            </button>
            {savedStyle && onClearSavedStyle && (
              <button
                type="button"
                onClick={onClearSavedStyle}
                className="text-foreground text-xs underline opacity-80 hover:opacity-100">
                Forget my saved default
              </button>
            )}
          </div>

          <p className="text-foreground text-xs opacity-80">
            {savedStyle
              ? 'New posts start from your saved default.'
              : 'New posts start from CASSI’s defaults until you save your own.'}
          </p>
        </div>
      )}

      {listed.length > 0 && (choice.nativeLabel || choice.visibleLabel) && (
        <div className="bg-muted space-y-2 rounded-lg p-3">
          <p className="text-foreground text-xs font-medium">What each platform will do</p>
          <ul className="space-y-1.5">
            {listed.map((platform) => {
              const { label, Icon } = PLATFORM_META[platform]
              return (
                <li key={platform} className="text-foreground flex items-start gap-2">
                  <span className="mt-0.5 shrink-0">
                    <Icon color="currentColor" width={13} height={13} />
                  </span>
                  <span className="text-xs">
                    <span className="font-medium">{label}</span>
                    <span className="opacity-80">
                      {' '}
                      — {describePlan(platform, choice, hasImageMedia)}
                    </span>
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
